import type { EditOperation } from '../chat/types';

/**
 * Read the current document's AI-authored tracked changes and group them
 * into logical edit operations for the Accept/Reject card UI.
 *
 * Why client-side:
 *   The DOCX itself already carries `w:ins` / `w:del` markup with author
 *   attribution. The plugin can enumerate them via Office JS and render
 *   cards without the AI agent having to re-describe each edit in the SSE
 *   payload. Simpler, less coupling.
 *
 * Grouping heuristic: adjacent tracked changes that live in the same
 * paragraph are collapsed into one card. A delete-then-insert pair becomes
 * a single "Replaced …" card. Standalone insertions or deletions become
 * their own cards.
 */

const AI_AUTHOR = 'NyayAssist AI';

/**
 * Narrow typing for the bits we load off `Word.TrackedChange`. Office JS's
 * public typings for this class are a bit loose around the enum values
 * (string-vs-enum), so we normalise to lowercase strings.
 */
interface LoadedChange {
  text: string;
  type: 'added' | 'deleted' | 'formatted' | 'none';
  author: string;
  paragraphIndex: number;
}

export async function readPendingAiEdits(): Promise<EditOperation[]> {
  let loaded: LoadedChange[] = [];
  try {
    loaded = await Word.run(async (context) => {
      const tc = context.document.body.getTrackedChanges();
      tc.load('items/author,items/type,items/text');
      await context.sync();

      // Load paragraph indexes in a separate pass so .getRange() works on
      // each proxy object correctly. Cheap: a single sync covers all.
      const ranges = tc.items.map((c) => c.getRange());
      const paragraphs = ranges.map((r) => r.paragraphs.getFirst());
      paragraphs.forEach((p) => p.load('text'));
      await context.sync();

      // Paragraphs don't expose index directly; we build one by walking the
      // body's paragraphs list once and comparing identity via text hash.
      const bodyParas = context.document.body.paragraphs;
      bodyParas.load('items/text');
      await context.sync();
      const paraHashes = bodyParas.items.map((p) => hashParagraph(p.text));

      return tc.items.map((c, i) => {
        const txt = paragraphs[i]?.text ?? '';
        const paraIdx = paraHashes.indexOf(hashParagraph(txt));
        return {
          text: c.text ?? '',
          type: normaliseType(c.type),
          author: c.author ?? '',
          paragraphIndex: paraIdx,
        } satisfies LoadedChange;
      });
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[pendingChangesReader] failed to load tracked changes', err);
    return [];
  }

  // Filter to AI-authored changes only — don't touch user hand-edits.
  const ai = loaded.filter((c) => c.author === AI_AUTHOR);
  if (ai.length === 0) return [];

  return groupIntoOperations(ai);
}

function normaliseType(t: unknown): LoadedChange['type'] {
  const s = String(t).toLowerCase();
  if (s === 'added' || s === 'deleted' || s === 'formatted' || s === 'none') return s;
  return 'none';
}

function hashParagraph(text: string): string {
  // Simple djb2 on trimmed text — enough for paragraph identity within
  // one read cycle. Not collision-safe across documents; good enough for
  // grouping tracked changes by paragraph.
  const s = (text ?? '').trim();
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return `${s.length}:${h}`;
}

/**
 * Collapse adjacent same-paragraph changes into single EditOperation cards.
 *
 * Rules:
 *   - A deletion immediately followed by an insertion in the same paragraph
 *     becomes one `replace` op (old = deleted text, new = inserted text).
 *   - Standalone insertion → `insert` op.
 *   - Standalone deletion → `delete` op.
 *   - Changes in different paragraphs never merge.
 */
function groupIntoOperations(changes: LoadedChange[]): EditOperation[] {
  const out: EditOperation[] = [];
  let i = 0;
  while (i < changes.length) {
    const cur = changes[i];
    const next = changes[i + 1];
    const sameParagraph =
      !!next &&
      next.paragraphIndex === cur.paragraphIndex &&
      cur.paragraphIndex >= 0;

    if (
      sameParagraph &&
      cur.type === 'deleted' &&
      next.type === 'added'
    ) {
      out.push(
        buildOp('replace', { oldText: cur.text, newText: next.text }, cur, i),
      );
      i += 2;
      continue;
    }
    if (
      sameParagraph &&
      cur.type === 'added' &&
      next.type === 'deleted'
    ) {
      out.push(
        buildOp('replace', { oldText: next.text, newText: cur.text }, cur, i),
      );
      i += 2;
      continue;
    }

    if (cur.type === 'added') {
      out.push(buildOp('insert', { oldText: '', newText: cur.text }, cur, i));
    } else if (cur.type === 'deleted') {
      out.push(buildOp('delete', { oldText: cur.text, newText: '' }, cur, i));
    }
    // Formatting and 'none' types are skipped — the UX focuses on text changes.
    i += 1;
  }
  return out;
}

function buildOp(
  editType: EditOperation['editType'],
  texts: { oldText: string; newText: string },
  anchor: LoadedChange,
  idx: number,
): EditOperation {
  // Stable-enough id: paragraph index + kind + text hashes. Survives reorders
  // as long as the paragraph itself doesn't move, which is usually the case
  // because Accept/Reject re-indexes paragraphs one at a time.
  const id = `ai-${anchor.paragraphIndex}-${editType}-${hashParagraph(
    `${texts.oldText}|${texts.newText}`,
  )}-${idx}`;
  return {
    id,
    description: describe(editType, texts),
    oldText: texts.oldText,
    newText: texts.newText,
    editType,
    changeAuthor: AI_AUTHOR,
    status: 'pending',
  };
}

function describe(
  editType: EditOperation['editType'],
  texts: { oldText: string; newText: string },
): string {
  const clip = (s: string) => {
    const t = s.replace(/\s+/g, ' ').trim();
    return t.length > 60 ? t.slice(0, 59) + '…' : t;
  };
  if (editType === 'insert') return `Inserted: ${clip(texts.newText)}`;
  if (editType === 'delete') return `Deleted: ${clip(texts.oldText)}`;
  return `Replaced: ${clip(texts.oldText)} → ${clip(texts.newText)}`;
}
